/**
 * Theme Preview
 * Full-page preview of a theme before purchasing/activating
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { sectionRegistry } from '../../components/theme-sections';
import ThemeStyleProvider, { mergeStyles } from '../../components/theme-sections/ThemeStyleProvider';
import { getThemeRenderer } from '../../themes/themeRendererRegistry';
import axios from '../../axiosConfig';
import { useToast } from '../../components/ui/Toast';
import './ThemePreview.css';

// SVG Icons
const ArrowLeftIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

const MonitorIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);

const TabletIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
    <path d="M12 18h.01" />
  </svg>
);

const SmartphoneIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <path d="M12 18h.01" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

// Device presets
const DEVICES = {
  desktop: { width: '100%', label: 'Desktop' },
  tablet: { width: '768px', label: 'Tablet' },
  mobile: { width: '375px', label: 'Mobile' },
};

function ThemePreview() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useSecureAuth();
  const { showToast } = useToast();

  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [device, setDevice] = useState('desktop');
  const [activating, setActivating] = useState(false);
  const [purchasedThemes, setPurchasedThemes] = useState([]);

  // Fetch theme data and purchased themes
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch theme preview (required)
        const themeRes = await axios.get(`/themes/${slug}/preview`);

        if (themeRes.data.success) {
          setTheme(themeRes.data.theme);
        } else {
          throw new Error('Theme not found');
        }

        // Fetch purchased themes (optional - don't fail if this errors)
        try {
          const purchasedRes = await axios.get('/themes/merchant/purchased');
          if (purchasedRes.data?.themes) {
            setPurchasedThemes(purchasedRes.data.themes);
          }
        } catch (purchaseErr) {
          // Silently ignore - user might not be logged in
          console.log('Could not fetch purchased themes:', purchaseErr.message);
        }
      } catch (error) {
        console.error('Failed to fetch theme:', error);
        showToast('Failed to load theme preview', 'error');
        navigate('/manage-store/customization');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, navigate, showToast]);

  // Check if theme is owned (free or purchased)
  const isOwned = () => {
    if (!theme) return false;
    if (theme.price === 0 || !theme.isPremium) return true;
    return purchasedThemes.some((p) => p.id === theme.id);
  };

  // Handle activate (for free/owned themes)
  const handleActivate = async () => {
    setActivating(true);
    try {
      const res = await axios.post(`/themes/${theme.id}/activate`);
      if (res.data.success) {
        showToast('Theme activated successfully', 'success');
        navigate('/manage-store/customization');
      }
    } catch (error) {
      console.error('Failed to activate theme:', error);
      showToast(error.response?.data?.error || 'Failed to activate theme', 'error');
    } finally {
      setActivating(false);
    }
  };

  // Handle purchase (for premium themes)
  const handlePurchase = async () => {
    setActivating(true);
    try {
      const callbackUrl = `${window.location.origin}/site/themes/purchase/callback`;
      const res = await axios.post(`/themes/${theme.id}/purchase`, { callbackUrl });

      if (res.data.success && res.data.paymentUrl) {
        // Redirect to Paystack payment page
        window.location.href = res.data.paymentUrl;
      } else {
        showToast(res.data.error || 'Failed to initialize payment', 'error');
      }
    } catch (error) {
      console.error('Failed to purchase theme:', error);
      showToast(error.response?.data?.error || 'Failed to process purchase', 'error');
    } finally {
      setActivating(false);
    }
  };

  // Handle button click - decide between activate or purchase
  const handleUseTheme = () => {
    if (isOwned()) {
      handleActivate();
    } else {
      handlePurchase();
    }
  };

  if (loading) {
    return (
      <div className="tp-loading">
        <div className="tp-loading__spinner" />
        <p>Loading preview...</p>
      </div>
    );
  }

  if (!theme) {
    return (
      <div className="tp-error">
        <p>Theme not found</p>
        <button onClick={() => navigate('/manage-store/customization')}>Back to customization</button>
      </div>
    );
  }

  const globalStyles = theme.globalSettings || {};
  const sections = theme.sections || [];
  // Premium themes (edition/atelier/gallery) ship their own HomePage renderer;
  // the classic section registry doesn't know their section types and would
  // otherwise render every section as a yellow "Unknown section" box. Mirror
  // the live storefront's ThemedHomePage branching so the preview matches
  // what a merchant will actually see after activation.
  const dedicatedRenderer = getThemeRenderer(theme);
  const DedicatedHomePage = dedicatedRenderer?.HomePage || null;
  const previewStore = {
    name: theme.name ? `${theme.name} preview` : 'Preview Store',
    slug: 'preview',
    currency: theme.currency || 'NGN'
  };
  const previewThemeConfig = {
    theme,
    globalOverrides: null,
    pages: [{ pageType: 'HOME', sections }]
  };

  return (
    <div className="tp-container">
      {/* Header */}
      <div className="tp-header">
        <button className="tp-back-btn" onClick={() => navigate('/manage-store/customization')}>
          <ArrowLeftIcon />
          Back
        </button>

        <div className="tp-header__center">
          <h1 className="tp-header__title">{theme.name}</h1>
          <span className="tp-header__industry">{theme.industry?.replace('_', ' ')}</span>
        </div>

        <div className="tp-header__right">
          {/* Device Toggle */}
          <div className="tp-device-toggle">
            <button
              className={`tp-device-btn ${device === 'desktop' ? 'tp-device-btn--active' : ''}`}
              onClick={() => setDevice('desktop')}
              title="Desktop"
            >
              <MonitorIcon />
            </button>
            <button
              className={`tp-device-btn ${device === 'tablet' ? 'tp-device-btn--active' : ''}`}
              onClick={() => setDevice('tablet')}
              title="Tablet"
            >
              <TabletIcon />
            </button>
            <button
              className={`tp-device-btn ${device === 'mobile' ? 'tp-device-btn--active' : ''}`}
              onClick={() => setDevice('mobile')}
              title="Mobile"
            >
              <SmartphoneIcon />
            </button>
          </div>

          <button
            className={`tp-activate-btn ${!isOwned() ? 'tp-activate-btn--purchase' : ''}`}
            onClick={handleUseTheme}
            disabled={activating}
          >
            {activating ? (
              <span className="tp-btn-spinner" />
            ) : isOwned() ? (
              <>
                <CheckIcon />
                Use This Theme
              </>
            ) : (
              <>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Purchase Theme {theme?.price > 0 && `(₦${(theme.price / 100).toLocaleString()})`}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Preview Frame */}
      <div className="tp-preview-wrapper">
        <div
          className="tp-preview-frame"
          style={{ maxWidth: DEVICES[device].width }}
        >
          <ThemeStyleProvider styles={globalStyles}>
            <div className="tp-preview-content">
              {DedicatedHomePage ? (
                <DedicatedHomePage
                  store={previewStore}
                  themeConfig={previewThemeConfig}
                  campaigns={[]}
                />
              ) : (
                <>
                  {sections.map((section) => {
                    const SectionComponent = sectionRegistry[section.type];

                    if (!SectionComponent) {
                      return (
                        <div key={section.id} className="tp-unknown-section">
                          Unknown section: {section.type}
                        </div>
                      );
                    }

                    return (
                      <SectionComponent
                        key={section.id}
                        settings={section.settings || {}}
                        content={section.content || {}}
                        globalStyles={globalStyles}
                        store={previewStore}
                        industry={theme.industry}
                      />
                    );
                  })}

                  {sections.length === 0 && (
                    <div className="tp-empty-sections">
                      <p>No sections configured for this theme</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </ThemeStyleProvider>
        </div>
      </div>
    </div>
  );
}

export default ThemePreview;
